
import { useState } from 'react';

const HomePage = () => {
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    message: ''
  });

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (contactForm.message.length > 500) {
      alert('Message must be 500 characters or less');
      return;
    }

    const formData = new URLSearchParams();
    formData.append('name', contactForm.name);
    formData.append('email', contactForm.email);
    formData.append('message', contactForm.message);

    try {
      const response = await fetch('https://readdy.ai/api/form/d4tccuitj9hepa8fj1m0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData
      });

      if (response.ok) {
        setContactForm({ name: '', email: '', message: '' });
        alert('Message sent successfully!');
      } else {
        alert('Failed to send message. Please try again.');
      }
    } catch (error) {
      alert('Error sending message. Please try again.');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setContactForm(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 transition-all duration-300 bg-white/95 backdrop-blur-sm shadow-sm">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center space-x-3">
              <img 
                src="https://public.readdy.ai/ai/img_res/c4e8ffdd-793b-4d28-8aa9-14da37e5dbc2.png" 
                alt="DIDIER S.R.L. Logo" 
                className="h-12 w-12 object-contain"
              />
              <span className="text-xl font-bold text-gray-900">DIDIER S.R.L.</span>
            </div>
            <div className="hidden md:flex space-x-8">
              <a href="#home" className="text-gray-700 hover:text-teal-600 font-medium transition-colors cursor-pointer">Home</a>
              <a href="#about" className="text-gray-700 hover:text-teal-600 font-medium transition-colors cursor-pointer">About</a>
              <a href="#services" className="text-gray-700 hover:text-teal-600 font-medium transition-colors cursor-pointer">VPN Services</a>
              <a href="#features" className="text-gray-700 hover:text-teal-600 font-medium transition-colors cursor-pointer">Features</a>
              <a href="#contact" className="text-gray-700 hover:text-teal-600 font-medium transition-colors cursor-pointer">Contact</a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-gradient-to-br from-slate-900 via-gray-900 to-slate-800"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/ef3294938639ce90a5eb0fc6036a0c17.jpg')`
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-white space-y-8">
              <div className="space-y-4">
                <h1 className="text-6xl lg:text-7xl font-extrabold leading-tight">
                  Secure Your
                  <br />
                  <span className="text-teal-400">Digital Life</span>
                </h1>
                <p className="text-xl text-gray-300 leading-relaxed max-w-2xl">
                  Protect your online privacy with our advanced VPN solutions. Experience secure, fast, and reliable internet connections worldwide.
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <button className="bg-white text-gray-900 px-8 py-4 rounded-full font-semibold hover:bg-gray-100 transition-all duration-300 whitespace-nowrap cursor-pointer">
                  Get Protected Now →
                </button>
                <button className="border-2 border-white text-white px-8 py-4 rounded-full font-semibold hover:bg-white hover:text-gray-900 transition-all duration-300 whitespace-nowrap cursor-pointer">
                  Learn More
                </button>
              </div>
              
              <div className="flex flex-wrap gap-6 pt-8">
                <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                  <span className="text-sm font-medium">🔒 256-bit Encryption</span>
                </div>
                <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                  <span className="text-sm font-medium">🚫 No-Log Policy</span>
                </div>
                <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                  <span className="text-sm font-medium">⏰ 24/7 Support</span>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=Professional%20laptop%20computer%20displaying%20VPN%20interface%20with%20security%20dashboard%2C%20network%20connections%2C%20and%20glowing%20protection%20indicators%2C%20modern%20tech%20setup%20with%20clean%20minimal%20background%2C%20high-tech%20cybersecurity%20visualization&width=600&height=400&seq=hero2&orientation=landscape"
                alt="VPN Interface"
                className="w-full h-auto rounded-lg shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <div className="inline-block">
                <span className="text-sm font-semibold text-teal-600 uppercase tracking-wide border border-teal-600 px-3 py-1 rounded-full">
                  About DIDIER S.R.L.
                </span>
              </div>
              
              <h2 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                Leading VPN
                <br />
                <span className="flex items-center gap-4">
                  Technology
                  <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center">
                    <i className="ri-shield-check-line text-white text-2xl"></i>
                  </div>
                </span>
                <br />
                Innovation
              </h2>
            </div>
            
            <div className="space-y-8">
              <div className="space-y-6">
                <p className="text-lg text-gray-700 leading-relaxed">
                  DIDIER S.R.L. is a cutting-edge technology company specializing in VPN application development. We create secure, reliable VPN solutions that protect users' digital privacy and online security across all platforms.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed">
                  Our commitment to cybersecurity excellence and user privacy drives us to develop innovative solutions that meet the highest industry standards, ensuring our clients can navigate the digital world with complete confidence.
                </p>
              </div>
              
              <div className="bg-white p-8 rounded-xl shadow-sm space-y-4">
                <div className="flex items-center space-x-4">
                  <i className="ri-mail-line text-teal-600 text-xl w-6 h-6 flex items-center justify-center"></i>
                  <span className="text-gray-900 font-medium">information@didiersrl.com</span>
                </div>
                <div className="flex items-center space-x-4">
                  <i className="ri-phone-line text-teal-600 text-xl w-6 h-6 flex items-center justify-center"></i>
                  <span className="text-gray-900 font-medium">+1 2245099409</span>
                </div>
                <div className="flex items-center space-x-4">
                  <i className="ri-map-pin-line text-teal-600 text-xl w-6 h-6 flex items-center justify-center"></i>
                  <span className="text-gray-900 font-medium">LISANDRO DE LA TORRE 1750, San Rafael, M5602EEJ, Argentina</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* VPN Services Section */}
      <section id="services" className="py-20 bg-slate-900 relative overflow-hidden">
        <div 
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='m36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}
        ></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="text-center space-y-6 mb-16">
            <div className="w-2 h-2 bg-teal-400 rounded-full mx-auto"></div>
            <h2 className="text-5xl lg:text-6xl font-extrabold text-white">Our VPN Services</h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Comprehensive VPN solutions designed to meet every security need, from individual privacy to enterprise-grade protection.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gray-800 p-8 rounded-3xl space-y-6 hover:transform hover:scale-105 transition-all duration-300">
              <div className="w-20 h-20 bg-gray-700 rounded-full flex items-center justify-center">
                <i className="ri-shield-user-line text-white text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-white">Personal VPN</h3>
              <p className="text-gray-400">
                Perfect for individual users who want to protect their personal data and browse the internet securely from any location.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center text-gray-300">
                  <i className="ri-check-line text-teal-400 mr-2"></i>
                  Easy setup and use
                </li>
                <li className="flex items-center text-gray-300">
                  <i className="ri-check-line text-teal-400 mr-2"></i>
                  Multiple device support
                </li>
                <li className="flex items-center text-gray-300">
                  <i className="ri-check-line text-teal-400 mr-2"></i>
                  Fast connection speeds
                </li>
              </ul>
            </div>
            
            <div className="relative overflow-hidden rounded-3xl">
              <div 
                className="absolute inset-0"
                style={{
                  backgroundImage: `url('https://readdy.ai/api/search-image?query=Modern%20business%20office%20environment%20with%20professionals%20working%20on%20computers%2C%20cybersecurity%20setup%20with%20multiple%20monitors%20showing%20network%20security%20data%2C%20corporate%20tech%20workspace%20with%20blue%20lighting%20accents&width=400&height=500&seq=business1&orientation=portrait')`
                }}
              >
                <div className="absolute inset-0 bg-black/60"></div>
              </div>
              <div className="relative z-10 p-8 space-y-6 h-full flex flex-col justify-between">
                <div className="bg-black/30 backdrop-blur-sm px-4 py-2 rounded-full inline-flex items-center space-x-2 w-fit">
                  <i className="ri-building-line text-white"></i>
                  <span className="text-white font-medium">BUSINESS</span>
                </div>
                <div className="space-y-4">
                  <h3 className="text-3xl font-bold text-white">Business VPN</h3>
                  <p className="text-gray-200">
                    Secure your business operations with our reliable VPN solutions designed for growing companies and remote teams.
                  </p>
                </div>
                <div className="w-1 h-16 bg-gradient-to-b from-teal-400 to-blue-500 rounded-full"></div>
              </div>
            </div>
            
            <div className="bg-white p-8 rounded-3xl space-y-6 hover:transform hover:scale-105 transition-all duration-300">
              <h3 className="text-3xl font-bold text-gray-900">Enterprise Solutions</h3>
              <p className="text-gray-600">
                Comprehensive security infrastructure for large organizations requiring advanced protection and compliance features.
              </p>
              <img 
                src="https://readdy.ai/api/search-image?query=Abstract%20network%20connections%20visualization%20with%20interconnected%20nodes%2C%20data%20flow%20diagrams%2C%20and%20security%20pathways%2C%20clean%20minimalist%20design%20with%20blue%20and%20teal%20color%20scheme%2C%20enterprise%20technology%20illustration&width=300&height=200&seq=enterprise1&orientation=landscape"
                alt="Enterprise Network"
                className="w-full h-32 object-cover rounded-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-amber-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center space-y-6 mb-16">
            <div className="absolute top-8 left-8">
              <span className="text-sm font-light text-gray-500 uppercase tracking-widest transform -rotate-90 origin-left">
                DIDIER S.R.L.
              </span>
            </div>
            <h2 className="text-5xl lg:text-6xl font-bold text-gray-900">Why Choose Our VPN</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Advanced security features and cutting-edge technology that sets us apart from the competition.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-3xl shadow-sm hover:shadow-lg transition-all duration-300 hover:transform hover:-translate-y-2 space-y-6">
              <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center">
                <i className="ri-lock-2-line text-white text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900">Advanced Encryption</h3>
              <p className="text-gray-600">
                Military-grade 256-bit encryption ensures your data remains completely secure and private during transmission.
              </p>
            </div>
            
            <div className="bg-white p-8 rounded-3xl shadow-sm hover:shadow-lg transition-all duration-300 hover:transform hover:-translate-y-2 space-y-6">
              <div className="w-16 h-16 bg-purple-500 rounded-full flex items-center justify-center">
                <i className="ri-speed-line text-white text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900">Lightning Fast</h3>
              <p className="text-gray-600">
                Optimized servers worldwide provide blazing-fast connection speeds without compromising security.
              </p>
            </div>
            
            <div className="bg-white p-8 rounded-3xl shadow-sm hover:shadow-lg transition-all duration-300 hover:transform hover:-translate-y-2 space-y-6">
              <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center">
                <i className="ri-global-line text-white text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900">Global Coverage</h3>
              <p className="text-gray-600">
                Access servers in multiple countries to bypass geo-restrictions and enjoy unrestricted internet access.
              </p>
            </div>
            
            <div className="bg-white p-8 rounded-3xl shadow-sm hover:shadow-lg transition-all duration-300 hover:transform hover:-translate-y-2 space-y-6">
              <div className="w-16 h-16 bg-teal-500 rounded-full flex items-center justify-center">
                <i className="ri-wifi-line text-white text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900">No Bandwidth Limits</h3>
              <p className="text-gray-600">
                Enjoy unlimited data usage with no throttling or restrictions on your internet activities.
              </p>
            </div>
            
            <div className="bg-white p-8 rounded-3xl shadow-sm hover:shadow-lg transition-all duration-300 hover:transform hover:-translate-y-2 space-y-6">
              <div className="w-16 h-16 bg-indigo-500 rounded-full flex items-center justify-center">
                <i className="ri-devices-line text-white text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900">Multi-Device Support</h3>
              <p className="text-gray-600">
                Protect all your devices simultaneously with our cross-platform compatibility and easy setup.
              </p>
            </div>
            
            <div className="bg-white p-8 rounded-3xl shadow-sm hover:shadow-lg transition-all duration-300 hover:transform hover:-translate-y-2 space-y-6">
              <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center">
                <i className="ri-server-line text-white text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900">Global Server Network</h3>
              <p className="text-gray-600">
                Strategically placed servers worldwide ensure optimal performance and reliable connections everywhere.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-0 bg-white rounded-3xl shadow-xl overflow-hidden">
            <div className="p-12 space-y-8">
              <div className="space-y-4">
                <h2 className="text-4xl lg:text-5xl font-bold text-gray-900">Get In Touch</h2>
                <p className="text-gray-600">
                  Have questions about our VPN services? We're here to help. Send us a message and we'll respond within 24 hours.
                </p>
              </div>
              
              <form onSubmit={handleContactSubmit} className="space-y-6" data-readdy-form id="contact">
                <div>
                  <input
                    type="text"
                    name="name"
                    value={contactForm.name}
                    onChange={handleInputChange}
                    placeholder="Your Name"
                    required
                    className="w-full px-4 py-4 text-sm border border-gray-200 rounded-lg focus:border-teal-500 focus:outline-none transition-colors"
                  />
                </div>
                <div>
                  <input
                    type="email"
                    name="email"
                    value={contactForm.email}
                    onChange={handleInputChange}
                    placeholder="Your Email"
                    required
                    className="w-full px-4 py-4 text-sm border border-gray-200 rounded-lg focus:border-teal-500 focus:outline-none transition-colors"
                  />
                </div>
                <div>
                  <textarea
                    name="message"
                    value={contactForm.message}
                    onChange={handleInputChange}
                    placeholder="Your Message (max 500 characters)"
                    maxLength={500}
                    rows={6}
                    required
                    className="w-full px-4 py-4 text-sm border border-gray-200 rounded-lg focus:border-teal-500 focus:outline-none transition-colors resize-none"
                  ></textarea>
                  <div className="text-right text-xs text-gray-500 mt-1">
                    {contactForm.message.length}/500 characters
                  </div>
                </div>
                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-teal-500 to-blue-500 text-white py-4 rounded-lg font-semibold hover:from-teal-600 hover:to-blue-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  Send Message ✈️
                </button>
              </form>
            </div>
            
            <div className="bg-slate-900 p-12 text-white space-y-8">
              <h3 className="text-2xl font-bold">Contact Information</h3>
              
              <div className="space-y-8">
                <div className="flex items-center space-x-4">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-mail-line text-teal-400 text-xl"></i>
                  </div>
                  <div>
                    <p className="font-medium">Email</p>
                    <p className="text-gray-300">information@didiersrl.com</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-4">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-phone-line text-teal-400 text-xl"></i>
                  </div>
                  <div>
                    <p className="font-medium">Phone</p>
                    <p className="text-gray-300">+1 2245099409</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-4">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-map-pin-line text-teal-400 text-xl"></i>
                  </div>
                  <div>
                    <p className="font-medium">Address</p>
                    <p className="text-gray-300">
                      LISANDRO DE LA TORRE 1750<br />
                      San Rafael, M5602EEJ<br />
                      Argentina
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="pt-8">
                <img 
                  src="https://readdy.ai/api/search-image?query=Modern%20communication%20technology%20icons%20with%20glowing%20effects%2C%203D%20rendered%20email%20envelope%2C%20phone%2C%20and%20location%20pin%20symbols%20floating%20in%20dark%20space%20with%20blue%20and%20teal%20lighting%2C%20cybersecurity%20contact%20visualization&width=300&height=200&seq=contact1&orientation=landscape"
                  alt="Contact Illustration"
                  className="w-full h-32 object-cover rounded-lg opacity-80"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <img 
                  src="https://public.readdy.ai/ai/img_res/c4e8ffdd-793b-4d28-8aa9-14da37e5dbc2.png" 
                  alt="DIDIER S.R.L. Logo" 
                  className="h-10 w-10 object-contain"
                />
                <span className="text-xl font-bold">DIDIER S.R.L.</span>
              </div>
              <p className="text-gray-400">Securing your digital freedom since 2025</p>
            </div>
            
            <div className="space-y-4">
              <h4 className="text-lg font-semibold">Company</h4>
              <ul className="space-y-2">
                <li><a href="#about" className="text-gray-400 hover:text-teal-400 transition-colors cursor-pointer">About</a></li>
                <li><a href="#services" className="text-gray-400 hover:text-teal-400 transition-colors cursor-pointer">Services</a></li>
                <li><a href="#features" className="text-gray-400 hover:text-teal-400 transition-colors cursor-pointer">Technology</a></li>
                <li><a href="#contact" className="text-gray-400 hover:text-teal-400 transition-colors cursor-pointer">Security</a></li>
              </ul>
            </div>
            
            <div className="space-y-4">
              <h4 className="text-lg font-semibold">Support</h4>
              <ul className="space-y-2">
                <li><a href="#contact" className="text-gray-400 hover:text-teal-400 transition-colors cursor-pointer">Help Center</a></li>
                <li><a href="#contact" className="text-gray-400 hover:text-teal-400 transition-colors cursor-pointer">Documentation</a></li>
                <li><a href="#contact" className="text-gray-400 hover:text-teal-400 transition-colors cursor-pointer">Contact</a></li>
                <li><a href="#contact" className="text-gray-400 hover:text-teal-400 transition-colors cursor-pointer">FAQ</a></li>
              </ul>
            </div>
            
            <div className="space-y-4 text-right">
              <div className="text-2xl font-bold">© DIDIER S.R.L. 2025</div>
              <p className="text-gray-400 text-sm">All rights reserved</p>
              <div className="space-x-4">
                <a href="https://readdy.ai/?origin=logo" className="text-teal-400 hover:underline cursor-pointer">Powered by Readdy</a>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-800 pt-8">
            <div className="grid md:grid-cols-3 gap-8 items-center">
              <div>
                <img 
                  src="https://readdy.ai/api/search-image?query=Modern%20technology%20workspace%20office%20with%20computers%20and%20cybersecurity%20setup%2C%20professional%20tech%20environment%20with%20blue%20accent%20lighting%2C%20clean%20minimal%20corporate%20interior%20design&width=320&height=200&seq=footer1&orientation=landscape"
                  alt="Office Workspace"
                  className="w-full h-32 object-cover rounded-xl border border-teal-500/30"
                />
              </div>
              
              <div className="text-center space-y-2">
                <h3 className="text-2xl font-bold text-gray-300">Protecting Digital Privacy</h3>
                <p className="text-gray-400">Worldwide Security Solutions</p>
                <p className="text-gray-400">Innovation & Excellence</p>
              </div>
              
              <div className="space-y-4">
                <h4 className="text-lg font-semibold">Stay Updated</h4>
                <p className="text-gray-400 text-sm">Get the latest security updates and VPN technology news delivered to your inbox.</p>
                <div className="flex">
                  <input
                    type="email"
                    placeholder="Enter your email"
                    className="flex-1 px-4 py-3 bg-gray-800 border border-gray-700 rounded-l-lg text-sm focus:outline-none focus:border-teal-500 transition-colors"
                  />
                  <button className="bg-teal-500 hover:bg-teal-600 px-6 py-3 rounded-r-lg font-medium transition-colors whitespace-nowrap cursor-pointer">
                    Subscribe →
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
